import { useState } from 'react';
import { toast } from '@cencosud-cencommerce/paris-uikit';

import { useRepository } from '@/Application/Providers/Repository';
import { setOnBehalf } from '@/modules/cart/application/setOnBehalf';
import type { AuthorizedPerson } from '@/modules/cart/domain/Cart';
import { type IdentificationDocumentType } from '@/modules/shared/types/IdentificationDocumentType';
import { useCustomerContext } from '@/sections/customer/CustomerContext';
import type { MutatorHook } from '@/sections/shared/types/MutatorHook';

interface Entity {
  deliveryGroupId: string;
  authorizedPerson: AuthorizedPerson;
  customerDocumentInfo?: {
    documentType?: IdentificationDocumentType;
    documentID?: string;
    customerRut?: string;
  };
}

const useSetOnBehalf = (): MutatorHook<Entity, void> & {
  toastMessages: typeof messages;
} => {
  const { cart: cartRepository } = useRepository();
  const {
    customer: { data: customer }
  } = useCustomerContext();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isRegistered = customer !== null;

  const messages = {
    success: {
      title: 'Otra persona hará el retiro',
      body: 'Sus datos se guardaron con éxito.'
    },
    successRemove: {
      title: 'Eliminaste los datos de retiro',
      body: 'Tú retirarás la compra.'
    },
    errorEdit: {
      title: 'No pudimos editar los datos de retiro',
      body: 'Por favor, vuelve a intentarlo.'
    },
    errorRemove: {
      title: 'No pudimos eliminar los datos de retiro',
      body: 'Por favor, intenta de nuevo.'
    }
  };

  const trigger = async ({
    deliveryGroupId,
    authorizedPerson,
    customerDocumentInfo
  }: Entity) => {
    setIsLoading(true);
    const onBehalf = await setOnBehalf(
      cartRepository,
      deliveryGroupId,
      authorizedPerson,
      customerDocumentInfo,
      isRegistered
    );
    if (onBehalf) {
      // Check if setting authorized person (new or legacy format)
      if (
        (authorizedPerson?.documentType && authorizedPerson?.documentID) ||
        authorizedPerson?.rut
      ) {
        toast(messages.success);
        setError(null);
      } else {
        // Removing authorized person
        toast(messages.successRemove);
        setError(null);
      }
    } else {
      // Check if error was while setting or removing
      if (
        (authorizedPerson?.documentType && authorizedPerson?.documentID) ||
        authorizedPerson?.rut
      ) {
        setError('Error trying to edit pickup id');
        toast({
          ...messages.errorEdit,
          status: 'error'
        });
        setIsLoading(false);
        throw 'Error trying to edit pickup id';
      } else {
        setError('Error trying to remove pickup id');
        toast({
          ...messages.errorRemove,
          status: 'error'
        });
        setIsLoading(false);
        throw 'Error trying to remove pickup id';
      }
    }
    setIsLoading(false);
  };

  return { trigger, isLoading, error, toastMessages: messages };
};

export default useSetOnBehalf;
